import { type ApolloQueryResult, useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { get } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../shared/permissions';
import { type FinderVehicleWithPermissionsSpecsFragment } from '../../../api/fragments/FinderVehicleWithPermissionsSpecs';
import {
    UpdateFinderVehicleDocument,
    type UpdateFinderVehicleMutation,
    type UpdateFinderVehicleMutationVariables,
} from '../../../api/mutations/updateFinderVehicle';
import { type GetVehicleQuery, type GetVehicleQueryVariables } from '../../../api/queries/getVehicle';
import hasPermissions from '../../../utilities/hasPermissions';
import Inner from './Inner';
import { type FinderVehicleFormValues } from './shared';
import useFinderVehicleValidator from './useFinderVehicleValidator';

type FinderVehicleFormProps = {
    finderVehicle: FinderVehicleWithPermissionsSpecsFragment;
    isPartnerNumberSetup: boolean;
    refetch: (variables?: Partial<GetVehicleQueryVariables>) => Promise<ApolloQueryResult<GetVehicleQuery>>;
};

const FinderVehicleForm = ({ finderVehicle, isPartnerNumberSetup, refetch }: FinderVehicleFormProps) => {
    const { t } = useTranslation(['finderVehicle']);
    const navigate = useNavigate();
    const apolloClient = useApolloClient();

    const onSubmit = useCallback(
        async (values: FinderVehicleFormValues) => {
            // submitting message
            message.loading({
                content: t('finderVehicle:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { id, setting, lta, isLTAUploaded } = values;

            await apolloClient
                .mutate<UpdateFinderVehicleMutation, UpdateFinderVehicleMutationVariables>({
                    mutation: UpdateFinderVehicleDocument,
                    variables: { id, payload: { setting, lta, isLTAUploaded } },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('finderVehicle:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate('/admin/finderVehicles');
        },
        [apolloClient, navigate, t]
    );

    // show only if the finder vehicle is preowned
    const hasLTA = useMemo(() => {
        const condition = get('listing.vehicle.condition.value', finderVehicle);

        const isPreowned = condition !== 'new';

        if (finderVehicle.module.__typename === 'FinderVehicleManagementModule') {
            return isPreowned && finderVehicle.module.setting.allowLTA;
        }

        return hasLTA;
    }, [finderVehicle]);

    const validate = useFinderVehicleValidator(hasLTA);
    const hasUpdateFinderVehiclePermission = useMemo(
        () => hasPermissions(finderVehicle.permissions, [permissionKind.updateFinderVehicle]),
        [finderVehicle.permissions]
    );

    return (
        <Formik<FinderVehicleFormValues>
            initialValues={finderVehicle}
            onSubmit={onSubmit}
            validate={validate}
            enableReinitialize
        >
            <Inner
                disabled={!hasUpdateFinderVehiclePermission}
                finderVehicleVin={finderVehicle.listing.vehicle.vin}
                hasLTA={hasLTA}
                isPartnerNumberSetup={isPartnerNumberSetup}
                refetch={refetch}
            />
        </Formik>
    );
};

export default FinderVehicleForm;
